/**
 * De dichte tabel onder de datasets. Hetzelfde filter als de kaart, sorteerbaar op
 * elke kolom die een waarde draagt. Adres, postcode, prijs, oppervlakte en kamers
 * sorteert de database via de keyset-cursor; alleen de labelvolgorde bestaat daar
 * niet en wordt hier geordend.
 *
 * Een woning zonder coordinaat staat hier wel - de brief zegt het met zoveel
 * woorden - en is als zodanig te herkennen: een stil tekentje bij het adres, en
 * onder 'zonder coordinaat' de reden erbij.
 *
 * Achteraan staan de keurknoppen. Dat is de tweede plek waar je een adres
 * beoordeelt - de eerste is het popupje op de kaart - en hier gaat het om snel
 * een lijst aflopen in plaats van één punt bekijken.
 *
 * Vooraan staat sinds MA-17 een vinkje. Wat aangevinkt is, is een groep: het
 * filter bepaalt wat er in beeld staat (één buurt, één prijsklasse, één dataset),
 * het kopvinkje pakt alles in beeld, en de balk eronder keurt of verwijdert die
 * groep in één keer. Verwijderen vraagt eerst om bevestiging, in tekst.
 */
import {
  AANBOD_LABELS,
  type Beoordeling,
  energielabelKleur,
  formatAantal,
  formatDatum,
  formatEuro,
  formatOppervlakte,
  type Listing,
} from '@mamimap/shared';
import { ChevronDown, ChevronUp, ExternalLink, MapPinOff } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Actiebalk } from '@/components/Actiebalk';
import { Beoordelingsknoppen } from '@/components/Beoordelingsknoppen';
import { EerderActief, OpnieuwAangeboden } from '@/components/Logboek';
import { Melding } from '@/components/Melding';
import { useBeoordelen, useWoningen } from '@/lib/api/queries';
import type { FilterState, Sortering } from '@/lib/filter-url';
import { allesAan, deelsAan, type Selectie, snoei, wissel, wisselAlles } from './selectie';
import { heeftPunt, ZONDER_PUNT_REDEN_TEKST, zonderPuntReden } from './zonder-punt';

type Kolom = {
  naam: string;
  /** Ascending and descending sort keys, when the column can be sorted. */
  sorteringen?: [Sortering, Sortering];
  /**
   * Welke kant de eerste klik op kiest. Bij een bedrag of een aantal wil je het
   * hoogste eerst; bij een naam of een postcode verwacht je A voor Z.
   */
  eerst?: 'op' | 'af';
  getal?: boolean;
};

const KOLOMMEN: Kolom[] = [
  { naam: 'Adres', sorteringen: ['adres_op', 'adres_af'], eerst: 'op' },
  { naam: 'Postcode', sorteringen: ['postcode_op', 'postcode_af'], eerst: 'op' },
  { naam: 'Vraagprijs', sorteringen: ['prijs_op', 'prijs_af'], eerst: 'af', getal: true },
  { naam: 'm²', sorteringen: ['oppervlakte_op', 'oppervlakte_af'], eerst: 'af', getal: true },
  { naam: '€/m²', sorteringen: ['prijs_per_m2_op', 'prijs_per_m2_af'], eerst: 'af', getal: true },
  { naam: 'Label', sorteringen: ['label_op', 'label_af'], eerst: 'op' },
  { naam: 'Kamers', sorteringen: ['kamers_op', 'kamers_af'], eerst: 'af', getal: true },
  { naam: 'Oordeel' },
  { naam: '' },
];

/** De kolom met de keurdatum; alleen waar die iets zegt (het Kansjes-scherm). */
const GEKEURD: Kolom = {
  naam: 'Gekeurd',
  sorteringen: ['gekeurd_op', 'gekeurd_af'],
  eerst: 'af',
};

type WoningTabelProps = {
  state: FilterState;
  /**
   * De kolom met de keurdatum erbij. Op het datasets-scherm staat daar bij bijna
   * elke rij een streepje; op het Kansjes-scherm is het juist de kolom waarop je
   * wilt ordenen.
   */
  toonGekeurd?: boolean;
};

export function WoningTabel({ state, toonGekeurd = false }: WoningTabelProps) {
  const { filter, sortering, zetSortering } = state;
  const woningen = useWoningen(filter, sortering);
  const beoordelen = useBeoordelen();
  const alleenZonder = filter.zonderCoordinaat === true;

  const [selectie, zetSelectie] = useState<Selectie>(() => new Set());
  const inBeeld = useMemo(() => woningen.rijen.map((woning) => woning.id), [woningen.rijen]);
  const isPending = woningen.isPending;

  /* Wat uit beeld raakt - ander filter, verwijderd - gaat uit de selectie. Alleen
     als de lijst klaar is, anders snoeit een lege tussenstand alles weg. */
  useEffect(() => {
    if (isPending) return;
    zetSelectie((huidig) => {
      const gesnoeid = snoei(huidig, inBeeld);
      return gesnoeid.size === huidig.size ? huidig : gesnoeid;
    });
  }, [inBeeld, isPending]);

  function sorteer(kolom: Kolom) {
    if (!kolom.sorteringen) return;
    const [op, af] = kolom.sorteringen;
    // Staat deze kolom al aan, dan draait de klik hem om; anders begint hij op de
    // kant die bij het soort waarde hoort: hoogste eerst bij een getal, A voor Z
    // bij een adres of een postcode.
    if (sortering === op) return zetSortering(af);
    if (sortering === af) return zetSortering(op);
    zetSortering(kolom.eerst === 'op' ? op : af);
  }

  return (
    <div className="flex min-h-0 flex-col">
      <Actiebalk
        className="mb-2"
        selectie={selectie}
        onWis={() => zetSelectie(new Set())}
        onKlaar={() => zetSelectie(new Set())}
      />

      <div className="min-h-0 flex-1 overflow-auto border border-line bg-surface-strong">
        <table className="tabel">
          <thead>
            <tr>
              <th className="w-7">
                <Kopvinkje
                  alles={allesAan(selectie, inBeeld)}
                  deels={deelsAan(selectie, inBeeld)}
                  uit={inBeeld.length === 0}
                  onWissel={() => zetSelectie(wisselAlles(selectie, inBeeld))}
                />
              </th>
              {kolommen(alleenZonder, toonGekeurd).map((kolom) => {
                const actief = kolom.sorteringen?.includes(sortering) ?? false;
                return (
                  <th
                    key={kolom.naam}
                    className={kolom.getal ? 'text-right' : undefined}
                    aria-sort={
                      actief ? (sortering.endsWith('_af') ? 'descending' : 'ascending') : undefined
                    }
                  >
                    {kolom.sorteringen ? (
                      <button
                        type="button"
                        onClick={() => sorteer(kolom)}
                        // `uppercase` omdat een knop de text-transform van de th
                        // niet overneemt: ADRES stond als 'Adres' tussen de
                        // kapitaaltjes van de datasettabel op hetzelfde scherm.
                        className={`inline-flex items-center gap-1 uppercase ${
                          actief ? 'text-ink' : 'hover:text-ink-soft'
                        }`}
                      >
                        {kolom.naam}
                        {actief ? (
                          sortering.endsWith('_af') ? (
                            <ChevronDown className="h-3 w-3" aria-hidden="true" />
                          ) : (
                            <ChevronUp className="h-3 w-3" aria-hidden="true" />
                          )
                        ) : null}
                      </button>
                    ) : (
                      kolom.naam
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {woningen.rijen.map((woning) => (
              <Rij
                key={woning.id}
                woning={woning}
                toonReden={alleenZonder}
                toonGekeurd={toonGekeurd}
                geselecteerd={selectie.has(woning.id)}
                onSelecteer={() => zetSelectie(wissel(selectie, woning.id))}
                bezig={beoordelen.isPending && beoordelen.variables?.id === woning.id}
                onKies={(beoordeling) => beoordelen.mutate({ id: woning.id, beoordeling })}
              />
            ))}
          </tbody>
        </table>

        {woningen.rijen.length === 0 && !woningen.isPending ? (
          <p className="px-3 py-3 text-[12px] text-muted">Geen woningen binnen dit filter.</p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-3 py-2 text-[12px] text-muted">
        {woningen.error ? <Melding fout={woningen.error} /> : null}
        {/* Eén mislukte keuring hoort niet elke rij rood te maken; hij staat hier,
            bij de rest van wat de tabel over zichzelf te melden heeft. */}
        {beoordelen.error ? <Melding fout={beoordelen.error} /> : null}
        <span className="cijfers">
          {formatAantal(woningen.rijen.length)} van{' '}
          {woningen.totaal === null ? '–' : formatAantal(woningen.totaal)}
        </span>
        {/* Onder 'zonder coordinaat': waarom ze er niet staan, geteld over precies
            de rijen die je nu ziet. */}
        {alleenZonder && woningen.volledigGeladen && woningen.rijen.length > 0 ? (
          <span className="text-faint">{redenen(woningen.rijen)}</span>
        ) : null}
        {/* Zelf zeven of ordenen kan niet eindeloos; dat het ergens ophoudt hoort
            op het scherm te staan in plaats van stil te gebeuren. */}
        {woningen.beperkt ? (
          <span className="text-brick">
            alleen de eerste {formatAantal(woningen.maxLokaal)} rijen doorzocht
          </span>
        ) : null}
        {/* Een rij die niet door het contract komt kost die rij, niet de tabel -
            maar hij verdwijnt niet stilzwijgend. */}
        {woningen.overgeslagen > 0 ? (
          <span className="text-faint">
            {woningen.overgeslagen === 1
              ? '1 rij overgeslagen'
              : `${formatAantal(woningen.overgeslagen)} rijen overgeslagen`}
          </span>
        ) : null}
        {woningen.hasNextPage ? (
          <button
            type="button"
            className="knop-stil"
            onClick={() => void woningen.fetchNextPage()}
            disabled={woningen.isFetchingNextPage}
          >
            Meer laden
          </button>
        ) : null}
      </div>
    </div>
  );
}

/** Het vinkje in de kop: aan, uit, of half - en `indeterminate` kan alleen via de DOM. */
function Kopvinkje({
  alles,
  deels,
  uit,
  onWissel,
}: {
  alles: boolean;
  deels: boolean;
  uit: boolean;
  onWissel: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = deels;
  }, [deels]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={alles}
      disabled={uit}
      onChange={onWissel}
      aria-label="Alles in beeld selecteren"
      className="h-3.5 w-3.5 accent-accent align-middle"
    />
  );
}

function Rij({
  woning,
  toonReden,
  toonGekeurd,
  geselecteerd,
  onSelecteer,
  bezig,
  onKies,
}: {
  woning: Listing;
  toonReden: boolean;
  toonGekeurd: boolean;
  geselecteerd: boolean;
  onSelecteer: () => void;
  bezig: boolean;
  onKies: (beoordeling: Beoordeling) => void;
}) {
  const punt = heeftPunt(woning);
  const reden = punt ? null : ZONDER_PUNT_REDEN_TEKST[zonderPuntReden(woning)];
  const uitleg = reden === null ? '' : `Niet op de kaart: ${reden.toLowerCase()}`;

  return (
    <tr data-gekozen={geselecteerd}>
      <td className="pr-0">
        <input
          type="checkbox"
          checked={geselecteerd}
          onChange={onSelecteer}
          aria-label={`${woning.adres} selecteren`}
          className="h-3.5 w-3.5 accent-accent align-middle"
        />
      </td>
      <Cel>
        <span className="inline-flex items-center gap-1.5">
          <span className={punt ? 'text-ink' : 'text-ink-soft'}>{woning.adres}</span>
          {/* Stil, maar niet onzichtbaar: deze woning staat niet op de kaart. */}
          {reden ? (
            <span title={uitleg} className="inline-flex items-center">
              <MapPinOff className="h-3 w-3 shrink-0 text-faint" aria-hidden="true" />
              <span className="sr-only">{uitleg}</span>
            </span>
          ) : null}
          <EerderActief compact terugkomsten={woning.terugkomsten} sinds={woning.aangemaaktOp} />
          <OpnieuwAangeboden
            compact
            opnieuwAangeboden={woning.opnieuwAangeboden}
            vraagprijs={woning.vraagprijs}
            vorigeVraagprijs={woning.vorigeVraagprijs}
          />
          {/* Alleen te zien als je er met het filter om vroeg (MA-22). */}
          {woning.aanbod !== 'actief' ? (
            <span className="text-[11px] text-brick">
              {AANBOD_LABELS[woning.aanbod].toLowerCase()}
            </span>
          ) : null}
        </span>
      </Cel>
      <Cel>{woning.postcode ?? '–'}</Cel>
      {toonReden ? <Cel>{reden ?? '–'}</Cel> : null}
      <Cel getal>{formatEuro(woning.vraagprijs)}</Cel>
      <Cel getal>
        {woning.oppervlakteM2 === null ? '–' : formatOppervlakte(woning.oppervlakteM2)}
      </Cel>
      <Cel getal>{woning.prijsPerM2 === null ? '–' : formatEuro(woning.prijsPerM2)}</Cel>
      <Cel>
        {woning.energielabel === null ? (
          <span className="text-faint">–</span>
        ) : (
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden="true"
              className="h-2 w-2"
              style={{ backgroundColor: energielabelKleur(woning.energielabel) }}
            />
            {woning.energielabel}
          </span>
        )}
      </Cel>
      <Cel getal>{woning.kamers === null ? '–' : formatAantal(woning.kamers)}</Cel>
      {toonGekeurd ? (
        <Cel>{woning.beoordeeldOp === null ? '–' : formatDatum(woning.beoordeeldOp)}</Cel>
      ) : null}
      <Cel>
        <Beoordelingsknoppen
          compact
          waarde={woning.beoordeling}
          omschrijving={woning.adres}
          bezig={bezig}
          onKies={onKies}
        />
      </Cel>
      <Cel>
        {woning.bronUrl ? (
          <a
            href={woning.bronUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="text-faint hover:text-accent"
            aria-label={`${woning.adres} op Funda`}
          >
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        ) : null}
      </Cel>
    </tr>
  );
}

function Cel({ children, getal }: { children: ReactNode; getal?: boolean }) {
  return <td className={getal ? 'cijfers text-right' : undefined}>{children}</td>;
}

/** 'Reden' en 'Gekeurd' bestaan alleen waar ze iets te zeggen hebben. */
function kolommen(metReden: boolean, metGekeurd: boolean): Kolom[] {
  let lijst = KOLOMMEN;
  if (metGekeurd) {
    // Vlak voor 'Oordeel', dus vlak voor de laatste twee: de datum hoort naast
    // de knoppen te staan die hem gezet hebben.
    lijst = [...lijst.slice(0, -2), GEKEURD, ...lijst.slice(-2)];
  }
  if (!metReden) return lijst;
  return [...lijst.slice(0, 2), { naam: 'Reden' }, ...lijst.slice(2)];
}

/** 'nieuwbouw 2 · adres niet gevonden 32', geteld over de zichtbare rijen. */
function redenen(rijen: readonly Listing[]): string {
  const tellingen = new Map<string, number>();
  for (const woning of rijen) {
    if (heeftPunt(woning)) continue;
    const tekst = ZONDER_PUNT_REDEN_TEKST[zonderPuntReden(woning)].toLowerCase();
    tellingen.set(tekst, (tellingen.get(tekst) ?? 0) + 1);
  }
  return [...tellingen.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([tekst, aantal]) => `${tekst} ${formatAantal(aantal)}`)
    .join(' · ');
}
